import {RefObject} from "react";
import {css} from "@panda/css";

import {Picture} from "@pg-design/picture-module";
import {Text} from "@pg-design/text-module";
import {navigationHeight} from "@pg-rp/navigation";

import {useBoundingClientRect} from "../../../hooks/use_bounding_client_rect";
import {IPersonalizedRecentSearch} from "../../types/IPersonalizedHomepage";
import {HomepageSearch} from "../HomepageSearch";
import {PersonalizedHomepageRecentSearches} from "./PersonalizedHomepageRecentSearches";

export interface IPersonalizedHomepageBackgroundImages {
    mobile: string;
    desktopSmall: string;
    desktopBig: string;
}

interface IProps {
    userName?: string | null;
    backgroundImages: IPersonalizedHomepageBackgroundImages;
    recentSearches: IPersonalizedRecentSearch[];
    areRecentSearchesLoading?: boolean;
    recentSearchesSectionRef?: RefObject<HTMLElement | null>;
    onRecentSearchClick?: (search: IPersonalizedRecentSearch, index: number) => void;
}

/**
 * Uses the same outer width contract as the public HomepageHeader
 * (`maxWidth: grid-lg`) so the personalized hero stretches to the content grid
 * instead of being double-padded by Container.
 */
export const PersonalizedHomepageHero = (props: IProps) => {
    const {userName, backgroundImages, recentSearches, areRecentSearchesLoading = false, recentSearchesSectionRef, onRecentSearchClick} = props;
    const {boundingClientRect: heroBoundingClientRect, elementRef: heroElementRef} = useBoundingClientRect<HTMLDivElement>();
    const greetingName = getGreetingName(userName);
    const blendHeight = `${(heroBoundingClientRect?.height ?? 0) + navigationHeight}px`;
    const blendTopPosition = heroBoundingClientRect ? `${Math.max(Math.floor(heroBoundingClientRect.height - navigationHeight), 0)}px` : "0px";

    return (
        <section className={heroStyle} aria-labelledby="personalized-homepage-heading">
            <div className={heroFrameStyle} ref={heroElementRef}>
                <div className={heroImageClipStyle}>
                    <Picture
                        sources={[
                            {minWidthPX: 0, src: backgroundImages.mobile, height: 836, width: 720},
                            {minWidthPX: 600, src: backgroundImages.desktopSmall, height: 919, width: 1920},
                            {minWidthPX: 1366, src: backgroundImages.desktopBig, height: 914, width: 2560}
                        ]}
                        alt=""
                        className={heroImageStyle}
                        fit="center center"
                        fetchPriority="high"
                    />
                    <div className={heroOverlayStyle} aria-hidden="true" />
                </div>

                <div className={heroContentStyle}>
                    <div className={heroWelcomeStyle}>
                        <Text id="personalized-homepage-heading" as="h1" fontFamily="secondary" align="center" className={heroHeadingStyle}>
                            {greetingName ? (
                                <>
                                    {/* Whitespace-only text nodes are ignored by the mobile flex column but keep "powrotem, Kacper" apart once the heading renders as a block (md+). */}
                                    <span className={heroHeadingLeadStyle}>Witaj z powrotem,</span> <span className={heroHeadingNameStyle}>{greetingName}</span>
                                </>
                            ) : (
                                "Witaj z powrotem!"
                            )}
                        </Text>
                    </div>

                    <div className={heroSearchStyle}>
                        <HomepageSearch
                            containerHeight={blendHeight}
                            blendTopPosition={blendTopPosition}
                            layoutVariant="hero"
                            submitMode="manual"
                            mobileSelectionMode="selectAndClose"
                        />
                    </div>
                </div>

                <PersonalizedHomepageRecentSearches
                    className={heroRecentSearchesStyle}
                    searches={recentSearches}
                    isLoading={areRecentSearchesLoading}
                    sectionRef={recentSearchesSectionRef}
                    onRecentSearchClick={onRecentSearchClick}
                />
            </div>
        </section>
    );
};

/** The profile name is a full name, the greeting addresses the user by the first name only. */
const getGreetingName = (userName?: string | null): string => (userName ?? "").trim().split(/\s+/)[0] ?? "";

const heroStyle = css({
    display: "flex",
    justifyContent: "center",
    boxSizing: "border-box",
    width: "100%",
    px: "1.2rem",
    pt: 2,

    sm: {
        px: 3,
        pt: 4
    },

    lg: {
        px: 4
    }
});

const heroFrameStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "1280px",
    minHeight: "38.2rem",
    px: 2,
    pt: 2,
    pb: 2,
    borderRadius: 3,
    overflow: "hidden",
    boxShadow: "2",

    sm: {
        maxWidth: "var(--grid-lg-widthPx)"
    },

    md: {
        minHeight: "54rem",
        px: 3,
        pt: 6,
        pb: 3
    }
});

const heroImageClipStyle = css({
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    borderRadius: 3
});

const heroImageStyle = css({
    width: "100%",
    height: "100%",

    "& img": {
        width: "100%",
        height: "100%",
        maxWidth: "none",
        objectFit: "cover"
    }
});

const heroOverlayStyle = css({
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(18, 22, 26, 0.86) 0%, rgba(18, 22, 26, 0.5) 50%, rgba(18, 22, 26, 0.28) 100%)",

    md: {
        background: "linear-gradient(90deg, rgba(35, 35, 45, 0.9) 0%, rgba(35, 35, 45, 0.62) 45%, rgba(35, 35, 45, 0.24) 100%)"
    }
});

/** Mobile: space-evenly keeps the same margin above and below the greeting. */
const heroContentStyle = css({
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    alignItems: "center",
    justifyContent: "space-evenly",
    gap: 0,
    width: "100%",

    md: {
        justifyContent: "center",
        gap: 8
    }
});

const heroWelcomeStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    width: "100%",

    md: {
        gap: 3
    }
});

const heroHeadingStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: 0,
    color: "var(--colors-white)",
    fontSize: "3.2rem",
    fontWeight: 700,
    lineHeight: "3.2rem",
    textShadow: "0 2px 16px rgba(0, 0, 0, 0.3)",

    md: {
        display: "block",
        fontSize: "4.8rem",
        lineHeight: "5.2rem",
        textShadow: "0 2px 20px rgba(0, 0, 0, 0.35)"
    },

    lg: {
        fontSize: "6.4rem"
    }
});

const heroHeadingLeadStyle = css({
    color: "var(--colors-white)"
});

const heroHeadingNameStyle = css({
    color: "var(--colors-primary)"
});

const heroSearchStyle = css({
    display: "flex",
    justifyContent: "center",
    width: "100%",

    md: {
        maxWidth: "77.6rem"
    }
});

const heroRecentSearchesStyle = css({
    position: "relative",
    zIndex: 1,
    marginTop: 2,

    md: {
        marginTop: 4
    }
});
import {RefObject} from "react";
import {css} from "@panda/css";

import {Picture} from "@pg-design/picture-module";
import {Text} from "@pg-design/text-module";
import {navigationHeight} from "@pg-rp/navigation";

import {useBoundingClientRect} from "../../../hooks/use_bounding_client_rect";
import {IPersonalizedRecentSearch} from "../../types/IPersonalizedHomepage";
import {HomepageSearch} from "../HomepageSearch";
import {PersonalizedHomepageRecentSearches} from "./PersonalizedHomepageRecentSearches";

export interface IPersonalizedHomepageBackgroundImages {
    mobile: string;
    desktopSmall: string;
    desktopBig: string;
}

interface IProps {
    userName?: string | null;
    backgroundImages: IPersonalizedHomepageBackgroundImages;
    recentSearches: IPersonalizedRecentSearch[];
    areRecentSearchesLoading?: boolean;
    recentSearchesSectionRef?: RefObject<HTMLElement | null>;
    onRecentSearchClick?: (search: IPersonalizedRecentSearch, index: number) => void;
}

/**
 * Uses the same outer width contract as the public HomepageHeader
 * (`maxWidth: grid-lg`) so the personalized hero stretches to the content grid
 * instead of being double-padded by Container.
 */
export const PersonalizedHomepageHero = (props: IProps) => {
    const {userName, backgroundImages, recentSearches, areRecentSearchesLoading = false, recentSearchesSectionRef, onRecentSearchClick} = props;
    const {boundingClientRect: heroBoundingClientRect, elementRef: heroElementRef} = useBoundingClientRect<HTMLDivElement>();
    const greetingName = getGreetingName(userName);
    const blendHeight = `${(heroBoundingClientRect?.height ?? 0) + navigationHeight}px`;
    const blendTopPosition = heroBoundingClientRect ? `${Math.max(Math.floor(heroBoundingClientRect.height - navigationHeight), 0)}px` : "0px";

    return (
        <section className={heroStyle} aria-labelledby="personalized-homepage-heading">
            <div className={heroFrameStyle} ref={heroElementRef}>
                <div className={heroImageClipStyle}>
                    <Picture
                        sources={[
                            {minWidthPX: 0, src: backgroundImages.mobile, height: 836, width: 720},
                            {minWidthPX: 600, src: backgroundImages.desktopSmall, height: 919, width: 1920},
                            {minWidthPX: 1366, src: backgroundImages.desktopBig, height: 914, width: 2560}
                        ]}
                        alt=""
                        className={heroImageStyle}
                        fit="center center"
                        fetchPriority="high"
                    />
                    <div className={heroOverlayStyle} aria-hidden="true" />
                </div>

                <div className={heroContentStyle}>
                    <div className={heroWelcomeStyle}>
                        <Text id="personalized-homepage-heading" as="h1" fontFamily="secondary" align="center" className={heroHeadingStyle}>
                            {greetingName ? (
                                <>
                                    {/* Whitespace-only text nodes are ignored by the mobile flex column but keep "powrotem, Kacper" apart once the heading renders as a block (md+). */}
                                    <span className={heroHeadingLeadStyle}>Witaj z powrotem,</span> <span className={heroHeadingNameStyle}>{greetingName}</span>
                                </>
                            ) : (
                                "Witaj z powrotem!"
                            )}
                        </Text>
                    </div>

                    <div className={heroSearchStyle}>
                        <HomepageSearch
                            containerHeight={blendHeight}
                            blendTopPosition={blendTopPosition}
                            layoutVariant="hero"
                            submitMode="manual"
                            mobileSelectionMode="selectAndClose"
                        />
                    </div>
                </div>

                <PersonalizedHomepageRecentSearches
                    className={heroRecentSearchesStyle}
                    searches={recentSearches}
                    isLoading={areRecentSearchesLoading}
                    sectionRef={recentSearchesSectionRef}
                    onRecentSearchClick={onRecentSearchClick}
                />
            </div>
        </section>
    );
};

/** The profile name is a full name, the greeting addresses the user by the first name only. */
const getGreetingName = (userName?: string | null): string => (userName ?? "").trim().split(/\s+/)[0] ?? "";

const heroStyle = css({
    display: "flex",
    justifyContent: "center",
    boxSizing: "border-box",
    width: "100%",
    px: "1.2rem",
    pt: 2,

    sm: {
        px: 3,
        pt: 4
    },

    lg: {
        px: 4
    }
});

const heroFrameStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "1280px",
    minHeight: "38.2rem",
    px: 2,
    pt: 2,
    pb: 2,
    borderRadius: 3,
    overflow: "hidden",
    boxShadow: "2",

    sm: {
        maxWidth: "var(--grid-lg-widthPx)"
    },

    md: {
        minHeight: "54rem",
        px: 3,
        pt: 6,
        pb: 3
    }
});

const heroImageClipStyle = css({
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    borderRadius: 3
});

const heroImageStyle = css({
    width: "100%",
    height: "100%",

    "& img": {
        width: "100%",
        height: "100%",
        maxWidth: "none",
        objectFit: "cover"
    }
});

const heroOverlayStyle = css({
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(18, 22, 26, 0.86) 0%, rgba(18, 22, 26, 0.5) 50%, rgba(18, 22, 26, 0.28) 100%)",

    md: {
        background: "linear-gradient(90deg, rgba(35, 35, 45, 0.9) 0%, rgba(35, 35, 45, 0.62) 45%, rgba(35, 35, 45, 0.24) 100%)"
    }
});

/** Mobile: space-evenly keeps the same margin above and below the greeting. */
const heroContentStyle = css({
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    alignItems: "center",
    justifyContent: "space-evenly",
    gap: 0,
    width: "100%",

    md: {
        justifyContent: "center",
        gap: 8
    }
});

const heroWelcomeStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    width: "100%",

    md: {
        gap: 3
    }
});

const heroHeadingStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: 0,
    color: "var(--colors-white)",
    fontSize: "3.2rem",
    fontWeight: 700,
    lineHeight: "3.2rem",
    textShadow: "0 2px 16px rgba(0, 0, 0, 0.3)",

    md: {
        display: "block",
        fontSize: "4.8rem",
        lineHeight: "5.2rem",
        textShadow: "0 2px 20px rgba(0, 0, 0, 0.35)"
    },

    lg: {
        fontSize: "6.4rem"
    }
});

const heroHeadingLeadStyle = css({
    color: "var(--colors-white)"
});

const heroHeadingNameStyle = css({
    color: "var(--colors-primary)"
});

const heroSearchStyle = css({
    display: "flex",
    justifyContent: "center",
    width: "100%",

    md: {
        maxWidth: "77.6rem"
    }
});

const heroRecentSearchesStyle = css({
    position: "relative",
    zIndex: 1,
    marginTop: 2,

    md: {
        marginTop: 4
    }
});
